import { useEffect, useMemo, useState } from 'react';
import { onSnapshot, query, where } from 'firebase/firestore';
import type { EntityDocument, EntityDocumentEntityType } from '../types';
import { docToEntityDocument, entityDocumentsCollectionRef } from '../lib/entityDocumentFirestore';

export function useEntityDocuments(
  userId: string | undefined,
  entityType: EntityDocumentEntityType,
  entityId: string | undefined
): {
  documents: EntityDocument[];
  loading: boolean;
  error: string | null;
} {
  const [documents, setDocuments] = useState<EntityDocument[]>([]);
  const [loading, setLoading] = useState(Boolean(userId && entityId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !entityId) {
      setDocuments([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      entityDocumentsCollectionRef(),
      where('userId', '==', userId),
      where('entityType', '==', entityType),
      where('entityId', '==', entityId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map(docToEntityDocument);
        next.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
        setDocuments(next);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, entityType, entityId]);

  return useMemo(() => ({ documents, loading, error }), [documents, loading, error]);
}
